// SignatureDialog.js
// "How will the customer sign?" — either the customer signs on this device
// (terms shown, name, agree, draw) or a sign-by-link is texted/emailed to
// them. Also manages a pending link (resend / cancel / sign here instead).
import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, TextField,
  Checkbox, FormControlLabel, RadioGroup, Radio, Alert, CircularProgress, Stack, Paper
} from '@mui/material';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import SendIcon from '@mui/icons-material/Send';
import DrawIcon from '@mui/icons-material/Gesture';
import FittedSignatureCanvas from '../RateAgreement/FittedSignatureCanvas';

const getAuthHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const STEP = { CHOOSE: 'choose', DEVICE: 'device', REMOTE: 'remote', PENDING: 'pending' };

const SignatureDialog = ({ open, onClose, jobId, job, signature, onSignatureChange }) => {
  const apiBase = `${process.env.REACT_APP_API_URL}/api/jobs/${jobId}`;
  const [step, setStep] = useState(STEP.CHOOSE);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // On-device state
  const [terms, setTerms] = useState(null);
  const [termsLoading, setTermsLoading] = useState(false);
  const [signerName, setSignerName] = useState('');
  const [agreed, setAgreed] = useState(false);
  const signatureRef = useRef(null);

  // Remote state
  const [channel, setChannel] = useState('sms');
  const [to, setTo] = useState('');

  const isPending = signature?.status === 'pending';

  // Reset whenever the dialog opens
  useEffect(() => {
    if (!open) return;
    setError(null);
    setBusy(false);
    setAgreed(false);
    setTerms(null);
    setSignerName(job?.customerName || '');
    setChannel(job?.customerPhone ? 'sms' : (job?.customerEmail ? 'email' : 'sms'));
    setTo(job?.customerPhone || job?.customerEmail || '');
    setStep(isPending ? STEP.PENDING : STEP.CHOOSE);
  }, [open, job, isPending]);

  // Switching channel swaps the prefilled destination
  const handleChannelChange = (value) => {
    setChannel(value);
    setTo(value === 'sms' ? (job?.customerPhone || '') : (job?.customerEmail || ''));
  };

  const loadTerms = useCallback(async () => {
    setTermsLoading(true);
    try {
      const res = await axios.get(`${apiBase}/terms`, { headers: getAuthHeader() });
      setTerms(res.data.terms);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not load the service terms.');
    } finally {
      setTermsLoading(false);
    }
  }, [apiBase]);

  useEffect(() => {
    if (open && step === STEP.DEVICE && !terms) loadTerms();
  }, [open, step, terms, loadTerms]);

  const saveDeviceSignature = async () => {
    setError(null);
    if (!signerName.trim()) { setError('Enter the customer\'s name.'); return; }
    if (!agreed) { setError('The customer must agree to the terms before signing.'); return; }
    if (!signatureRef.current || signatureRef.current.isEmpty()) { setError('Draw a signature before saving.'); return; }
    setBusy(true);
    try {
      const res = await axios.post(`${apiBase}/signature`, {
        signatureData: signatureRef.current.toDataURL('image/png'),
        signerName: signerName.trim(),
        termsVersion: terms?.version,
        termsServiceKey: terms?.serviceKey
      }, { headers: getAuthHeader() });
      onSignatureChange?.(res.data.signature, 'signed');
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Could not save the signature.');
    } finally {
      setBusy(false);
    }
  };

  const sendLink = async (overrideChannel, overrideTo) => {
    setError(null);
    const ch = overrideChannel || channel;
    const dest = (overrideTo ?? to).trim();
    if (!dest) { setError(ch === 'sms' ? 'Enter a phone number.' : 'Enter an email address.'); return; }
    setBusy(true);
    try {
      const res = await axios.post(`${apiBase}/signature-request`, { channel: ch, to: dest }, { headers: getAuthHeader() });
      onSignatureChange?.(res.data.signature, 'sent');
      setStep(STEP.PENDING);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not send the signing link.');
    } finally {
      setBusy(false);
    }
  };

  const cancelRequest = async () => {
    setError(null);
    setBusy(true);
    try {
      const res = await axios.delete(`${apiBase}/signature-request`, { headers: getAuthHeader() });
      onSignatureChange?.(res.data.signature, 'cancelled');
      setStep(STEP.CHOOSE);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not cancel the request.');
    } finally {
      setBusy(false);
    }
  };

  const title = {
    [STEP.CHOOSE]: 'How will the customer sign?',
    [STEP.DEVICE]: 'Customer signs on this device',
    [STEP.REMOTE]: 'Send a signing link',
    [STEP.PENDING]: 'Signature request pending'
  }[step];

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        {step === STEP.CHOOSE && (
          <Stack spacing={1.5}>
            <Paper variant="outlined" sx={{ p: 2, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }} onClick={() => setStep(STEP.DEVICE)}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <DrawIcon color="primary" />
                <Box>
                  <Typography variant="subtitle1">On this device</Typography>
                  <Typography variant="body2" color="text.secondary">Show the terms and hand the device to the customer to sign now.</Typography>
                </Box>
              </Box>
            </Paper>
            <Paper variant="outlined" sx={{ p: 2, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }} onClick={() => setStep(STEP.REMOTE)}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <PhoneIphoneIcon color="primary" />
                <Box>
                  <Typography variant="subtitle1">Send to another device</Typography>
                  <Typography variant="body2" color="text.secondary">Text or email the customer a link to review and sign on their own phone.</Typography>
                </Box>
              </Box>
            </Paper>
          </Stack>
        )}

        {step === STEP.DEVICE && (
          <Box>
            {termsLoading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={28} /></Box>}
            {terms && (
              <>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{terms.title}</Typography>
                <Box
                  sx={{
                    mt: 1, p: 1.5, maxHeight: 220, overflowY: 'auto', border: '1px solid', borderColor: 'divider',
                    borderRadius: 1, whiteSpace: 'pre-wrap', fontSize: '0.85rem', lineHeight: 1.5
                  }}
                >
                  {terms.body}
                </Box>
              </>
            )}
            <TextField
              label="Customer name"
              value={signerName}
              onChange={(e) => setSignerName(e.target.value)}
              fullWidth
              margin="normal"
              required
              inputProps={{ maxLength: 120 }}
            />
            <FormControlLabel
              control={<Checkbox checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />}
              label="The customer has read and agrees to these terms"
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Have the customer sign below (finger or mouse):
            </Typography>
            <Box sx={{ position: 'relative', width: '100%', height: 180, border: '1px dashed', borderColor: 'text.disabled', borderRadius: 1, bgcolor: '#fff', touchAction: 'none' }}>
              <FittedSignatureCanvas ref={signatureRef} height={180} />
            </Box>
            <Button size="small" sx={{ mt: 0.5 }} onClick={() => signatureRef.current?.clear()}>Clear</Button>
          </Box>
        )}

        {step === STEP.REMOTE && (
          <Box>
            <RadioGroup row value={channel} onChange={(e) => handleChannelChange(e.target.value)}>
              <FormControlLabel value="sms" control={<Radio />} label="Text message (SMS)" />
              <FormControlLabel value="email" control={<Radio />} label="Email" />
            </RadioGroup>
            <TextField
              label={channel === 'sms' ? 'Mobile number' : 'Email address'}
              value={to}
              onChange={(e) => setTo(e.target.value)}
              fullWidth
              margin="normal"
              type={channel === 'sms' ? 'tel' : 'email'}
              autoComplete="off"
            />
            <Typography variant="body2" color="text.secondary">
              The customer receives a link to review the terms and sign. The link expires in 48 hours.
            </Typography>
          </Box>
        )}

        {step === STEP.PENDING && (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              Link sent to <strong>{signature?.sentTo}</strong>
              {signature?.sentAt ? ` on ${new Date(signature.sentAt).toLocaleString()}` : ''} (pending).
            </Alert>
            <Typography variant="body2" color="text.secondary">
              Waiting for the customer to sign. You can resend the same link, send it somewhere else, cancel it, or have the customer sign on this device instead.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ flexWrap: 'wrap', gap: 0.5 }}>
        {busy && <CircularProgress size={20} sx={{ mr: 1 }} />}
        {step === STEP.CHOOSE && <Button onClick={onClose}>Close</Button>}

        {step === STEP.DEVICE && (
          <>
            <Button onClick={() => setStep(isPending ? STEP.PENDING : STEP.CHOOSE)} disabled={busy}>Back</Button>
            <Button variant="contained" onClick={saveDeviceSignature} disabled={busy || termsLoading || !terms}>Save Signature</Button>
          </>
        )}

        {step === STEP.REMOTE && (
          <>
            <Button onClick={() => setStep(isPending ? STEP.PENDING : STEP.CHOOSE)} disabled={busy}>Back</Button>
            <Button variant="contained" startIcon={<SendIcon />} onClick={() => sendLink()} disabled={busy}>Send</Button>
          </>
        )}

        {step === STEP.PENDING && (
          <>
            <Button color="error" onClick={cancelRequest} disabled={busy}>Cancel request</Button>
            <Button onClick={() => setStep(STEP.REMOTE)} disabled={busy}>Send elsewhere</Button>
            <Button onClick={() => sendLink(signature?.channel || channel, signature?.sentTo || to)} disabled={busy}>Resend</Button>
            <Button onClick={() => setStep(STEP.DEVICE)} disabled={busy}>Sign here instead</Button>
            <Button variant="contained" onClick={onClose} disabled={busy}>Done</Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SignatureDialog;
